/**
 * The PageSpeed call budget on Settings > Integrations > PageSpeed
 * (docs/saidiseire-kiirus-turve-raport.md, item C, "Call budget"): what was
 * used today and this month against the limits, and the two limits a manager
 * may change.
 *
 * The day is Pacific time, because Google resets the key's daily quota at
 * midnight in California (about 10:00 in Tallinn); the month is the Tallinn
 * calendar month. One site's weekly run is two calls, mobile and desktop, and
 * the engine measures a site only when both still fit, so the hint says so.
 * Whether the budget is spent is the API's answer (`spent`), by that same
 * rule, never worked out here from the counts: "24 999 / 25 000" is spent
 * too. An odd per-day limit gets a line of its own, since it leaves one call
 * a day unused. Google's per-minute limit is not a budget here: one line
 * says it and that the engine stays far under it.
 *
 * The limits are saved through the keys route (`pagespeedLimits`), which
 * validates them again and audits the change; the key itself is not touched.
 * The whole tab already needs `manage:crm.monitoring`, so there is no second
 * permission check here.
 */
import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  PAGESPEED_CALLS_PER_MINUTE,
  PAGESPEED_CALLS_PER_WAKE,
  PAGESPEED_LIMIT_BOUNDS,
  limitLeavesUnused,
  parseLimitInput,
  saveMonitorKeys,
  spentNoteKey,
  type MonitorKeysView,
  type PagespeedBudgetView,
} from "../../../../lib/monitoringKeys";
import { Btn, fmtNum } from "../../../ui";
import { Field, Note, Pill, SaveRow } from "../panel/fields";

export function PagespeedBudget({ budget, onSaved }: { budget: PagespeedBudgetView; onSaved: (view: MonitorKeysView) => void }) {
  const t = useT();
  const [perDay, setPerDay] = useState(String(budget.perDay));
  const [perMonth, setPerMonth] = useState(String(budget.perMonth));
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  // A save (or a fresh read) brings new stored limits: the boxes follow them.
  useEffect(() => {
    setPerDay(String(budget.perDay));
    setPerMonth(String(budget.perMonth));
  }, [budget.perDay, budget.perMonth]);

  const day = parseLimitInput("perDay", perDay);
  const month = parseLimitInput("perMonth", perMonth);
  const changed = day !== budget.perDay || month !== budget.perMonth;
  const valid = day !== null && month !== null;
  const spentNote = spentNoteKey(budget.spent);

  const save = async () => {
    if (day === null || month === null) return;
    setSaveState("saving");
    try {
      onSaved(await saveMonitorKeys({ pagespeedLimits: { perDay: day, perMonth: month } }));
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  };

  // Said in the error colour: the save button is off until the number fits, and the hint says why.
  const bad = (field: "perDay" | "perMonth") => (
    <span className="text-[var(--color-destructive)]">
      {t("settings.pagespeed.limitBad", { min: fmtNum(PAGESPEED_LIMIT_BOUNDS[field].min), max: fmtNum(PAGESPEED_LIMIT_BOUNDS[field].max) })}
    </span>
  );
  // An odd per-day limit leaves one call a day that no whole site can use.
  const dayHint = limitLeavesUnused(day)
    ? `${t("settings.pagespeed.perDayHint")} ${t("settings.pagespeed.perDayOdd")}`
    : t("settings.pagespeed.perDayHint");
  const edit = (set: (v: string) => void) => (v: string) => {
    set(v);
    setSaveState("idle");
  };

  return (
    <section className="mt-6 space-y-4 border-t border-[var(--crm-hair)] pt-5">
      <div>
        <h4 className="crm-lab !mt-0 uppercase tracking-[0.07em]">{t("settings.pagespeed.budgetTitle")}</h4>
        <p className="mt-1 text-[14px] font-medium">
          {t("settings.pagespeed.usage", {
            day: fmtNum(budget.dayCalls),
            perDay: fmtNum(budget.perDay),
            month: fmtNum(budget.monthCalls),
            perMonth: fmtNum(budget.perMonth),
          })}
        </p>
        <p className="crm-sub mt-1 text-[13px]">{t("settings.pagespeed.perSite")}</p>
        <p className="crm-sub mt-1 text-[13px]">
          {t("settings.pagespeed.perMinute", { perMinute: fmtNum(PAGESPEED_CALLS_PER_MINUTE), perWake: fmtNum(PAGESPEED_CALLS_PER_WAKE) })}
        </p>
      </div>

      {spentNote ? <Note tone="warn">{t(spentNote)}</Note> : null}

      <div className="grid min-w-0 gap-4 sm:grid-cols-2">
        <Field label={t("settings.pagespeed.perDay")} hint={day === null ? bad("perDay") : dayHint}>
          <Pill value={perDay} onChange={edit(setPerDay)} type="number" inputMode="numeric" min={PAGESPEED_LIMIT_BOUNDS.perDay.min} />
        </Field>
        <Field label={t("settings.pagespeed.perMonth")} hint={month === null ? bad("perMonth") : t("settings.pagespeed.perMonthHint")}>
          <Pill value={perMonth} onChange={edit(setPerMonth)} type="number" inputMode="numeric" min={PAGESPEED_LIMIT_BOUNDS.perMonth.min} />
        </Field>
      </div>

      <SaveRow>
        <Btn onClick={save} disabled={!changed || !valid || saveState === "saving"}>
          <Save className="size-4" /> {t("settings.pagespeed.saveLimits")}
        </Btn>
        {saveState === "saved" ? <span className="crm-sub">{t("common.saved")}</span> : null}
        {saveState === "error" ? <span className="crm-sub">{t("common.error")}</span> : null}
      </SaveRow>
    </section>
  );
}
